import useSWR from "swr";
import axios from "axios";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertCircle } from "lucide-react";
import {
  format,
  formatDistanceStrict,
  isWithinInterval,
  subDays,
} from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import React, { useState } from "react";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router";
import Loading from "@/components/ui/loading";
import { useSession } from "@clerk/clerk-react";
import { getUnexpiredToken } from "@/utils/utils";

const fetcher = (url: string, session: any) => {
  return getUnexpiredToken(session).then((supabaseJWT: string) =>
    axios
      .get(url, {
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
        },
      })
      .then((res) => res.data),
  );
};

interface ConfirmModalProps {
  actionText: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onAction: () => void;
}
const ConfirmModal: React.FC<ConfirmModalProps> = ({
  actionText,
  open,
  setOpen,
  onAction,
}) => {
  return (
    <AlertDialog onOpenChange={setOpen} open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently change this
            user's permissions to this project.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="pt-4">
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => onAction()}>
            {actionText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const LoadingState = () => {
  return (
    <div className="flex items-center justify-center py-16">
      <Loading size={32} />
    </div>
  );
};

function RequestPage() {
  const navigate = useNavigate();
  const params = useParams();
  const token = params.token;

  const { session } = useSession();

  const { data: request, error } = useSWR(
    token && session
      ? `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/projects/access-requests/${token}`
      : null,
    (url) => fetcher(url, session),
  );

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);

  if (error)
    return (
      <div className="p-16">
        <Alert variant="default">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            You don't have access to this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  if (!session || !request) return <LoadingState />;

  const handleUpdateStatus = async (_projectId: string, status: string) => {
    const supabaseJWT = await getUnexpiredToken(session);

    try {
      await axios.patch(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/projects/${_projectId}/access/${token}`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      );
      toast.success(`Reviewed request`);
      setTimeout(() => {
        navigate("/");
      }, 100);
    } catch (error) {
      console.error(error);
      toast.error("Failed to review request");
    }
  };

  const { data } = request;

  if (data?.accessRequest?.status !== "pending") {
    return (
      <div className="p-16">
        <Alert variant="default">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Already reviewed</AlertTitle>
          <AlertDescription>
            This request has already been processed.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const now = new Date();
  const threeDaysAgo = subDays(now, 3);
  const createdAt = new Date(data.accessRequest.created_at);

  let dateString = null;

  if (isWithinInterval(createdAt, { start: threeDaysAgo, end: now })) {
    // @ts-expect-error TS2322
    dateString = formatDistanceStrict(createdAt, now, { addSuffix: true });
  } else {
    // @ts-expect-error TS2322
    dateString = format(createdAt, "PPP");
  }

  const projectId = data.project.id;

  const projectKeys = {
    "Project Name": data.project.name,
    "Github Repository": data.project.repoPath,
  };

  const requestKeys = {
    Name: data.accessRequest.requester_name,
    Email: data.accessRequest.requester_email,
    Date: dateString,
  };

  return (
    <div className="flex items-center justify-center py-16">
      {showRejectModal && (
        <ConfirmModal
          actionText="Reject"
          open={showRejectModal}
          setOpen={setShowRejectModal}
          onAction={() => handleUpdateStatus(projectId, "rejected")}
        />
      )}
      {showApproveModal && (
        <ConfirmModal
          actionText="Approve"
          open={showApproveModal}
          setOpen={setShowApproveModal}
          onAction={() => handleUpdateStatus(projectId, "approved")}
        />
      )}
      <Card className="w-1/2">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl">Grant Access</CardTitle>
          <CardDescription>
            As an owner of this project, you have the ability to grant access to
            other users.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableCaption>Project and Request Information</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[400px]">Key</TableHead>
                <TableHead className="flex">Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(projectKeys).map(([key, value]) => (
                <TableRow key={key} className="bg-secondary-100">
                  <TableCell className="font-medium">{key}</TableCell>
                  <TableCell>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              {Object.entries(requestKeys).map(([key, value]) => (
                <TableRow key={key} className="bg-secondary-200">
                  <TableCell className="font-medium">{key}</TableCell>
                  <TableCell>{value}</TableCell>
                </TableRow>
              ))}
            </TableFooter>
          </Table>
        </CardContent>
        <CardFooter className="flex flex-row gap-x-4 justify-end">
          <Button
            className="w-32 bg-secondary-200 hover:bg-secondary-300"
            onClick={() => setShowRejectModal(true)}
          >
            Reject
          </Button>
          <Button
            className="w-32 bg-primary-0 hover:bg-primary-100"
            onClick={() => setShowApproveModal(true)}
          >
            Approve
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

export default RequestPage;
